/**
 * Lightweight tabs. No Radix dep. Controlled by a single `value` + `onChange`.
 */
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface TabsProps<T extends string> {
    value: T;
    onChange: (v: T) => void;
    tabs: { value: T; label: string }[];
    className?: string;
}

export function Tabs<T extends string>({ value, onChange, tabs, className }: TabsProps<T>) {
    return (
        <div className={cn('flex flex-wrap gap-1 border-b', className)}>
            {tabs.map((t) => {
                const active = t.value === value;
                return (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => onChange(t.value)}
                        className={cn(
                            'px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                            active
                                ? 'border-primary text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

interface TabPanelProps {
    when: boolean;
    children: ReactNode;
}

export function TabPanel({ when, children }: TabPanelProps) {
    if (!when) return null;
    return <div className="pt-4">{children}</div>;
}
